import { AlertTriangle, Search, Clock, CheckCircle, AlertCircle, Loader2, Eye, Edit, Trash2, Check, X, Camera } from "lucide-react"
import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Layout } from "@/components/Layout"
import NovaOcorrenciaModal from "@/components/NovaOcorrenciaModal"
import DetalhesOcorrenciaModal from "@/components/DetalhesOcorrenciaModal"
import RespostaAdministracaoModal from "@/components/RespostaAdministracaoModal"
import { useIncidents, useUpdateIncident, useDeleteIncident } from "@/hooks/useIncidents"
import { useAuth } from "@/hooks/useAuth"

const getStatusIcon = (status: string) => {
  switch (status) {
    case "closed":
      return <CheckCircle className="h-4 w-4 text-green-500" />
    case "completed":
      return <CheckCircle className="h-4 w-4 text-green-600" />
    case "approved_by_admin":
      return <Check className="h-4 w-4 text-blue-500" />
    case "approved_by_sindico":
      return <Clock className="h-4 w-4 text-orange-500" />
    case "rejected_by_admin":
      return <X className="h-4 w-4 text-red-600" />
    case "in_progress":
      return <Clock className="h-4 w-4 text-yellow-500" />
    case "open":
      return <AlertCircle className="h-4 w-4 text-red-500" />
    default:
      return <AlertTriangle className="h-4 w-4 text-gray-500" />
  }
}

const getStatusVariant = (status: string) => {
  switch (status) {
    case "closed":
    case "completed":
      return "default" as const
    case "approved_by_admin":
      return "secondary" as const
    case "approved_by_sindico":
      return "secondary" as const
    case "rejected_by_admin":
      return "destructive" as const
    case "in_progress":
      return "secondary" as const
    case "open":
      return "destructive" as const
    default:
      return "outline" as const
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case "closed":
      return "Fechada"
    case "completed":
      return "Concluída"
    case "approved_by_admin":
      return "Aprovada pela Administração"
    case "approved_by_sindico":
      return "Aprovada pelo Síndico"
    case "rejected_by_admin":
      return "Rejeitada pela Administração"
    case "in_progress":
      return "Em Andamento"
    case "open":
      return "Aberta"
    default:
      return "Desconhecido"
  }
}

const getPriorityVariant = (priority: string) => {
  switch (priority) {
    case "urgent":
      return "destructive" as const
    case "high":
      return "destructive" as const
    case "medium":
      return "default" as const
    case "low":
      return "secondary" as const
    default:
      return "outline" as const
  }
}

const getPriorityLabel = (priority: string) => {
  switch (priority) {
    case "urgent":
      return "Urgente"
    case "high":
      return "Alta"
    case "medium":
      return "Média"
    case "low":
      return "Baixa"
    default:
      return "Não definida"
  }
}

const Ocorrencias = () => {
  const { data: ocorrencias, isLoading, error } = useIncidents()
  const { profile } = useAuth()
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedIncident, setSelectedIncident] = useState<any>(null)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [showRespostaModal, setShowRespostaModal] = useState(false)
  const [acaoAdministracao, setAcaoAdministracao] = useState<'aprovar' | 'rejeitar'>('aprovar')

  const updateIncident = useUpdateIncident()
  const deleteIncident = useDeleteIncident()

  const canCreateIncidents = profile?.role !== 'morador' && profile?.role !== 'administracao'
  const isSindico = profile?.role === 'sindico' || profile?.role === 'superadmin'
  const isAdministracao = profile?.role === 'administracao'

  const filteredOcorrencias = ocorrencias?.filter(ocorrencia => {
    const searchLower = searchTerm.toLowerCase()
    const title = ocorrencia.title || ""
    const description = ocorrencia.description || ""
    const reporterName = ocorrencia.reporter?.full_name || ""
    const unitNumber = ocorrencia.unit?.number || ""
    
    return title.toLowerCase().includes(searchLower) ||
           description.toLowerCase().includes(searchLower) ||
           reporterName.toLowerCase().includes(searchLower) ||
           unitNumber.includes(searchTerm)
  }) || []

  const handleStatusUpdate = async (incidentId: string, newStatus: string) => {
    try {
      await updateIncident.mutateAsync({ id: incidentId, status: newStatus })
    } catch (error) {
      console.error("Erro ao atualizar status:", error)
    }
  }

  const handleDelete = async (incidentId: string) => {
    if (confirm("Tem certeza que deseja excluir esta ocorrência?")) {
      try {
        await deleteIncident.mutateAsync(incidentId)
      } catch (error) {
        console.error("Erro ao excluir ocorrência:", error)
      }
    }
  }

  const handleViewDetails = (incident: any) => {
    setSelectedIncident(incident)
    setShowDetailsModal(true)
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      </Layout>
    )
  }

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-center text-red-600">
            Erro ao carregar ocorrências: {error.message}
          </div>
        </div>
      </Layout>
    )
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ita-black">
              {profile?.role === 'morador' ? 'Minhas Ocorrências' : 'Ocorrências'}
            </h1>
            <p className="text-gray-600">
              {profile?.role === 'morador' 
                ? 'Visualize suas ocorrências reportadas' 
                : 'Gerencie as ocorrências do condomínio'
              }
            </p>
          </div>
          {canCreateIncidents && <NovaOcorrenciaModal />}
        </div>

        <div className="flex items-center space-x-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input 
              placeholder="Buscar ocorrências..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {filteredOcorrencias.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">
              {searchTerm ? "Nenhuma ocorrência encontrada" : "Nenhuma ocorrência cadastrada"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredOcorrencias.map((ocorrencia) => (
              <Card key={ocorrencia.id} className="ita-card">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <AlertTriangle className="h-5 w-5 text-ita-blue" />
                      <CardTitle className="text-lg font-semibold">
                        {ocorrencia.title}
                      </CardTitle>
                    </div>
                    <div className="flex flex-col gap-1">
                      <Badge variant={getStatusVariant(ocorrencia.status || "open")}>
                        <div className="flex items-center space-x-1">
                          {getStatusIcon(ocorrencia.status || "open")}
                          <span>{getStatusLabel(ocorrencia.status || "open")}</span>
                        </div>
                      </Badge>
                      <Badge variant={getPriorityVariant(ocorrencia.priority || "medium")}>
                        {getPriorityLabel(ocorrencia.priority || "medium")}
                      </Badge>
                    </div>
                  </div>
                  <CardDescription>
                    {ocorrencia.unit?.number ? `Unidade ${ocorrencia.unit.number}` : "Área comum"}
                    {profile?.role !== 'morador' && ocorrencia.reporter && (
                      <span> • Por: {ocorrencia.reporter.full_name}</span>
                    )}
                    {ocorrencia.assigned_to_user && (
                      <span> • Direcionado para: {ocorrencia.assigned_to_user.full_name}</span>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-700">{ocorrencia.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {new Date(ocorrencia.created_at || "").toLocaleDateString('pt-BR')}
                    </p>
                    {ocorrencia.photos && ocorrencia.photos.length > 0 && (
                      <div className="flex items-center mt-2">
                        <Camera className="h-4 w-4 text-gray-500 mr-1" />
                        <span className="text-xs text-gray-500">
                          {ocorrencia.photos.length} foto{ocorrencia.photos.length > 1 ? 's' : ''}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleViewDetails(ocorrencia)}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Ver
                    </Button>
                    
                    {/* Botões para Síndicos */}
                    {isSindico && ocorrencia.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(ocorrencia.id, 'approved_by_sindico')}
                        disabled={updateIncident.isPending}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Aprovar
                      </Button>
                    )}
                    
                    {/* Botões para Administração */}
                    {isAdministracao && ocorrencia.status === 'approved_by_sindico' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedIncident(ocorrencia)
                            setAcaoAdministracao('aprovar')
                            setShowRespostaModal(true)
                          }}
                          disabled={updateIncident.isPending}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Aprovar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedIncident(ocorrencia)
                            setAcaoAdministracao('rejeitar')
                            setShowRespostaModal(true)
                          }}
                          disabled={updateIncident.isPending}
                        >
                          <X className="h-4 w-4 mr-1" />
                          Rejeitar
                        </Button>
                      </>
                    )}
                    
                    {/* Botões para completar processo (administração) */}
                    {isAdministracao && ocorrencia.status === 'approved_by_admin' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStatusUpdate(ocorrencia.id, 'completed')}
                        disabled={updateIncident.isPending}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Concluir
                      </Button>
                    )}
                    
                    {/* Botão de exclusão para síndicos */}
                    {isSindico && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(ocorrencia.id)}
                        disabled={deleteIncident.isPending}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Excluir
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {showDetailsModal && selectedIncident && (
          <DetalhesOcorrenciaModal
            open={showDetailsModal}
            onOpenChange={setShowDetailsModal}
            ocorrencia={{
              id: selectedIncident.id,
              titulo: selectedIncident.title,
              descricao: selectedIncident.description,
              categoria: selectedIncident.category || "Sem categoria",
              prioridade: getPriorityLabel(selectedIncident.priority || "medium"),
              status: getStatusLabel(selectedIncident.status || "open"),
              unidade: selectedIncident.unit?.number || "Área comum",
              relator: selectedIncident.reporter?.full_name || "Desconhecido",
              dataAbertura: selectedIncident.created_at,
              dataUltimaAtualizacao: selectedIncident.updated_at,
              justificativa: selectedIncident.resolution_notes || "",
              fotos: selectedIncident.photos || []
            }}
            canEdit={isSindico || isAdministracao}
          />
        )}

        {showRespostaModal && selectedIncident && (
          <RespostaAdministracaoModal
            open={showRespostaModal}
            onOpenChange={setShowRespostaModal}
            ocorrencia={{
              id: selectedIncident.id,
              title: selectedIncident.title,
              status: selectedIncident.status
            }}
            acao={acaoAdministracao}
          />
        )}
      </div>
    </Layout>
  )
}

export default Ocorrencias
